import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useCreateConfig, useUpdateConfig } from "../../queries/appConfigs";
import RichTextEditor from "../../components/Forms/RichTextEditor";

const ConfigModal = ({ isOpen, onClose, config = null, adminType = null }) => {
    const isEditing = !!config;
    const isSuperAdmin = adminType === "superadmin";
    
    const { mutateAsync: createConfig, isPending: isCreating } = useCreateConfig();
    const { mutateAsync: updateConfig, isPending: isUpdating } = useUpdateConfig();
    
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
        getValues
    } = useForm({
        defaultValues: {
            key: "",
            value: "",
            type: "string",
            page: ""
        }
    });

    // Watch the type field to provide appropriate input types
    const selectedType = watch("type");

    // Populate form when editing
    useEffect(() => {
        if (config) {
            setValue("key", config.key);
            setValue("value", config.value);
            setValue("type", config.type);
            setValue("page", config.page || "");
        } else {
            reset();
        }
    }, [config, setValue, reset]);

    const onSubmit = async (data) => {
        try {
            if (isEditing) {
                await updateConfig({
                    id: config._id,
                    ...data
                });
                toast.success("Configuration updated successfully!");
            } else {
                await createConfig(data);
                toast.success("Configuration created successfully!");
            }
            onClose();
        } catch (error) {
            console.error("Error saving config:", error);
            toast.error(error.message || "Failed to save configuration. Please try again.");
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    if (!isOpen) return null;

    const isLoading = isCreating || isUpdating;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-[40vw] max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {isEditing ? "Edit Configuration" : "Create New Configuration"}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isLoading}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                    {/* Key Field */}
                    <div className="space-y-1">
                        <label className="block text-base font-medium text-gray-700">
                            Key <span className="text-red">*</span>
                        </label>
                        <input
                            type="text"
                            {...register("key", { 
                                required: "Key is required",
                                pattern: {
                                    value: /^[a-zA-Z0-9_.-]+$/,
                                    message: "Key can only contain letters, numbers, dots, hyphens, and underscores"
                                }
                            })}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                errors.key ? "border-red" : "border-border"
                            } ${!isSuperAdmin ? "bg-gray-100 cursor-not-allowed opacity-60" : ""}`}
                            placeholder="e.g., app.version, feature.enabled"
                            disabled={isLoading || !isSuperAdmin}
                        />
                        {errors.key && (
                            <p className="text-base text-red-600">{errors.key.message}</p>
                        )}
                    </div>

                    {/* Type Field */}
                    <div className="space-y-1">
                        <label className="block text-base font-medium text-gray-700">
                            Type <span className="text-red">*</span>
                        </label>
                        <select
                            {...register("type", { required: "Type is required" })}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                errors.type ? "border-red" : "border-border"
                            } ${!isSuperAdmin ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                            disabled={isLoading || !isSuperAdmin}
                        >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="json">JSON</option>
                            <option value="url">URL</option>
                            <option value="email">Email</option>
                            <option value="html">HTML</option>
                        </select>
                        {errors.type && (
                            <p className="text-base text-red-600">{errors.type.message}</p>
                        )}
                    </div>

                    {/* Page Field - Only visible to superadmin */}
                    {isSuperAdmin && (
                        <div className="space-y-1">
                            <label className="block text-base font-medium text-gray-700">
                                Page
                            </label>
                            <input
                                type="text"
                                {...register("page")}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                    errors.page ? "border-red" : "border-border"
                                }`}
                                placeholder="e.g., dashboard, profile, settings"
                                disabled={isLoading}
                            />
                            {errors.page && (
                                <p className="text-base text-red-600">{errors.page.message}</p>
                            )}
                            <p className="text-xs text-gray-500">
                                Optional field to specify which page this configuration applies to
                            </p>
                        </div>
                    )}

                    {/* Value Field */}
                    <div className="space-y-1">
                        <label className="block text-base font-medium text-gray-700">
                            Value <span className="text-red">*</span>
                        </label>
                        
                        {selectedType === "boolean" ? (
                            <select
                                {...register("value", { required: "Value is required" })}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                    errors.value ? "border-red" : "border-border"
                                }`}
                                disabled={isLoading}
                            >
                                <option value="">Select boolean value</option>
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </select>
                        ) : selectedType === "json" ? (
                            <textarea
                                {...register("value", { 
                                    required: "Value is required",
                                    validate: (value) => {
                                        try {
                                            JSON.parse(value);
                                            return true;
                                        } catch {
                                            return "Please enter valid JSON";
                                        }
                                    }
                                })}
                                rows={4}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                    errors.value ? "border-red" : "border-border"
                                }`}
                                placeholder='{"key": "value"}'
                                disabled={isLoading}
                            />
                        ) : selectedType === "html" ? (
                            <div className={`${errors.value ? "border border-red rounded-md" : ""}`}>
                                <RichTextEditor
                                    label=""
                                    value={watch("value")}
                                    placeholder="Enter HTML content..."
                                    onChange={(value) => {
                                        setValue("value", value);
                                        // Clear any existing validation errors when user types
                                        if (errors.value) {
                                            // Re-validate manually
                                            const currentValue = value;
                                            if (currentValue && currentValue.trim() !== "" && currentValue !== "<p><br></p>") {
                                                // Clear error by setting a valid value
                                                setValue("value", currentValue, { shouldValidate: true });
                                            }
                                        }
                                    }}
                                    required={false}
                                />
                                {/* Hidden input for form validation */}
                                <input
                                    type="hidden"
                                    {...register("value", { 
                                        required: "HTML content is required",
                                        validate: (value) => {
                                            // Check if the value is empty or just contains empty paragraph tags
                                            if (!value || value.trim() === "" || value === "<p><br></p>") {
                                                return "HTML content is required";
                                            }
                                            return true;
                                        }
                                    })}
                                />
                            </div>
                        ) : (
                            <input
                                type={
                                    selectedType === "number" ? "number" :
                                    selectedType === "email" ? "email" :
                                    selectedType === "url" ? "url" : "text"
                                }
                                {...register("value", { 
                                    required: "Value is required",
                                    ...(selectedType === "email" && {
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "Please enter a valid email address"
                                        }
                                    }),
                                    ...(selectedType === "url" && {
                                        pattern: {
                                            value: /^https?:\/\/.+/i,
                                            message: "Please enter a valid URL (http:// or https://)"
                                        }
                                    })
                                })}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                    errors.value ? "border-red" : "border-border"
                                }`}
                                placeholder={
                                    selectedType === "number" ? "123" :
                                    selectedType === "email" ? "admin@example.com" :
                                    selectedType === "url" ? "https://example.com" :
                                    "Enter configuration value"
                                }
                                disabled={isLoading}
                            />
                        )}
                        {errors.value && (
                            <p className="text-base text-red-600">{errors.value.message}</p>
                        )}
                    </div>

                    {/* Type-specific help text */}
                    <div className="text-xs text-gray-500">
                        {selectedType === "string" && "Any text value"}
                        {selectedType === "number" && "Numeric values only"}
                        {selectedType === "boolean" && "true or false values"}
                        {selectedType === "json" && "Valid JSON object or array"}
                        {selectedType === "url" && "Must start with http:// or https://"}
                        {selectedType === "email" && "Valid email address format"}
                        {selectedType === "html" && "Rich HTML content with formatting options"}
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4 border-t border-border">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 px-4 py-2 text-gray-700 bg-navBtn rounded-md hover:bg-border transition-colors"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    {isEditing ? "Updating..." : "Creating..."}
                                </span>
                            ) : (
                                isEditing ? "Update Configuration" : "Create Configuration"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ConfigModal;
